import React, { useEffect, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap';
import useUtenti from '../../../hooks/useUtenti';
import useAgenzie from '../../../hooks/useAgenzie';
import SelectWithSearch from '../../SelectWithSearch/SelectWithSearch';

const UpdateAgenziaForm = ({userData, onClose, printAllUtenti}) => {
   const {updateUtente, loadingStateUtenti} = useUtenti();
   const { getAllAgenzie } = useAgenzie();
   const [agenzie, setAgenzie] = useState([]);
   const [selectedAgenziaId, setSelectedAgenziaId] = useState(null);
   const [isLoadingAgenzie, setIsLoadingAgenzie] = useState(false);

  useEffect(() => {
    // Carica tutte le agenzie all'avvio
    const fetchAgenzie = async () => {
      setIsLoadingAgenzie(true);
      try {
        const response = await getAllAgenzie();
        setAgenzie(response || []);
      } catch (error) {
        console.error('Errore nel recupero delle agenzie:', error);
      } finally {
        setIsLoadingAgenzie(false);
      }
    };
    fetchAgenzie();
  }, []);

  useEffect(() => {
    // Inizializza selectedAgenziaId se l'utente ha già un'agenzia
    if (userData?.id_agenzia) {
      setSelectedAgenziaId(userData.id_agenzia);
    } else {
      setSelectedAgenziaId(null);
    }
  }, [userData])

  const onUpdateAgenzia = async () => {
    // IMPORTANTE: Il backend aggiorna TUTTI i campi, quindi dobbiamo passare tutti i dati esistenti
    // per evitare di sovrascrivere i valori con null
    const dataToUpdate = {
      id_utente: userData.id_utente,
      username: userData.username, // Campo obbligatorio richiesto dal backend
      id_agenzia: selectedAgenziaId || null,
      // Passiamo tutti i campi esistenti per evitare che vengano sovrascritti con null
      nome: userData.nome || null,
      cognome: userData.cognome || null,
      titolo: userData.titolo || null,
      sesso: userData.sesso || null,
      data_nascita: userData.data_nascita || null,
      provincia_nascita: userData.provincia_nascita || null,
      luogo_nascita: userData.luogo_nascita || null,
      telefono1: userData.telefono1 || null,
      telefono2: userData.telefono2 || null,
      note: userData.note || null,
      utente: userData.utente || null,
      abilitato_web: userData.abilitato_web || null,
      id_superpartner: userData.id_superpartner || null,
      fatturazione_nominativo: userData.fatturazione_nominativo || null,
      fatturazione_indirizzo: userData.fatturazione_indirizzo || null,
      fatturazione_cap: userData.fatturazione_cap || null,
      fatturazione_citta: userData.fatturazione_citta || null,
      fatturazione_provincia: userData.fatturazione_provincia || null,
      fatturazione_piva: userData.fatturazione_piva || null,
      spedizione_nominativo: userData.spedizione_nominativo || null,
      spedizione_indirizzo: userData.spedizione_indirizzo || null,
      spedizione_cap: userData.spedizione_cap || null,
      spedizione_citta: userData.spedizione_citta || null,
      spedizione_provincia: userData.spedizione_provincia || null,
      path_documento: userData.path_documento || null,
      path_codicefiscale: userData.path_codicefiscale || null,
      path_documento_retro: userData.path_documento_retro || null,
      path_codicefiscale_retro: userData.path_codicefiscale_retro || null,
      id_ut_jampink: userData.id_ut_jampink || null,
      eliminato: userData.eliminato || 0
    };
    
    const res = await updateUtente(dataToUpdate);
    if(res) {
      onClose();
      printAllUtenti();
    }
  }

  const getAgenziaLabel = (agenzia) => {
    if (!agenzia) return '';
    const parts = [agenzia.denominazione];
    if (agenzia.indirizzo) parts.push(agenzia.indirizzo);
    if (agenzia.cap || agenzia.citta || agenzia.provincia) {
      const location = [agenzia.cap, agenzia.citta, agenzia.provincia].filter(Boolean).join(', ');
      if (location) parts.push(`(${location})`);
    }
    return parts.join(' ');
  };

  // Trova l'agenzia attualmente associata
  const agenziaAttuale = userData?.id_agenzia 
    ? agenzie.find(a => a.id_agenzia === userData.id_agenzia)
    : null;

  return (
    <div className='row'>
      <div className='col-12 my-3' style={{ position: 'relative' }}>
        <span className='small text-muted d-block mb-2'><b>Utente: {userData?.nome} {userData?.cognome}</b></span>
        <span className='small text-muted d-block mb-3'>Seleziona o modifica l'agenzia associata all'utente</span>
        
        {/* Mostra l'agenzia attualmente associata se presente */}
        {agenziaAttuale && (
          <div className='alert alert-info mb-3' role='alert'>
            <i className='bi bi-info-circle me-2'></i>
            <strong>Agenzia attualmente associata:</strong>
            <div className='mt-2'>
              <span className='badge bg-primary me-2'>{agenziaAttuale.denominazione}</span>
              {agenziaAttuale.indirizzo && (
                <span className='small d-block mt-1'>
                  {agenziaAttuale.indirizzo}
                  {agenziaAttuale.cap && `, ${agenziaAttuale.cap}`}
                  {agenziaAttuale.citta && ` ${agenziaAttuale.citta}`}
                  {agenziaAttuale.provincia && ` (${agenziaAttuale.provincia})`}
                </span>
              )}
            </div>
          </div>
        )}
        
        <SelectWithSearch
          options={agenzie}
          value={selectedAgenziaId}
          onChange={setSelectedAgenziaId}
          placeholder="Cerca o seleziona un'agenzia..."
          label="Agenzia"
          getOptionLabel={getAgenziaLabel}
          getOptionValue={(option) => option.id_agenzia}
          searchFields={['denominazione', 'indirizzo', 'citta', 'provincia']}
          isLoading={isLoadingAgenzie}
        />
        
        {!selectedAgenziaId && (
          <span className='small text-muted d-block mb-3 mt-2'>Nessuna agenzia selezionata. L'utente non sarà associato ad alcuna agenzia.</span>
        )}
      </div>
      
      <div className='mt-4'>
        { loadingStateUtenti.isLoadingUtentiUpdate ? (
          <Button variant="primary" disabled className="mx-1">
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Salva modifiche...
          </Button>
        ) : (
          <Button type="button" variant='primary' onClick={onUpdateAgenzia} className="mx-1">
            Salva modifiche
          </Button>
        )}
        <button className='btn btn-secondary mx-1' onClick={onClose}>Annulla</button>
      </div>
    </div>
  )
}

export default UpdateAgenziaForm;
